import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiBox } from 'react-icons/fi';
import { graphqlRequest } from '../../../../utils/graphqlRequest';

/**
 * "Resources Allocated / Used" table on the project Overview.
 *
 * Reuses the allocation feed that ResourceUpdate/AllocationDashboard reads
 * (findAllInventoryManagement) and rolls the movement rows up per tool, because
 * one tool can have several movements against the same project (issued, then
 * returned or marked used). The updateAction vocabulary is the one the
 * inventory form writes — see InventoryUpdate.js.
 */
const PROJECT_INVENTORY = `
  query ProjectInventoryAllocation($projectId: String, $bookingId: String) {
    getResourcesByProject(projectId: $projectId, bookingId: $bookingId) {
      id
      quantityAssigned
      category
      updateAction
      remark
      status
      assignedAt
      assignedBy
      requestedBy
      tool { id name code unit category }
      project { id projectId }
    }
  }
`;

/** Cleaner work reports for the project — their "Resource update" step records usage. */
const CLEANER_REPORTS = `
  query ProjectResourceUsage($bookingId: String!, $projectId: String) {
    approvedWorkReportsForBooking(bookingId: $bookingId, projectId: $projectId, includePending: true) {
      id
      userId
      notesJson
    }
  }
`;

/** Resource rows a cleaner saved; portal versions used different keys. */
export function reportedResources(notesJson) {
  let notes = {};
  try {
    notes = JSON.parse(notesJson || '{}') || {};
  } catch {
    return [];
  }
  const manual = notes.manualReport || {};
  return (
    [manual.resourceUpdates, manual.resources, notes.resourceUpdates].find(
      (rows) => Array.isArray(rows) && rows.length,
    ) || []
  );
}

/** Project stock movements keyed by id, in the shape visitResources reads. */
export const allocationMap = (rows) =>
  new Map(
    (rows || []).map((r) => [
      String(r.id),
      {
        id: String(r.id),
        name: r.tool?.name || 'Resource',
        type: `${r.updateAction || 'Inventory'} Resource`,
        qty: r.quantityAssigned,
      },
    ]),
  );

/** What the cleaner reported on a visit, then every other resource allocated to the project. */
export function visitResources(notesJson, allocations) {
  const used = reportedResources(notesJson);
  const usedIds = new Set(used.map((r) => String(r?.id)));
  return [
    ...used.map((r) => ({ ...r, assigned: r?.assigned ?? allocations.get(String(r?.id))?.qty })),
    ...Array.from(allocations.values())
      .filter((a) => !usedIds.has(a.id))
      .map((a) => ({ id: a.id, name: a.name, type: a.type, assigned: a.qty })),
  ];
}

const norm = (value) => String(value || '').trim().toLowerCase();

/** Movements that hand stock out to a job (everything except stock-in/return/adjust). */
function isIssueLike(action) {
  const a = norm(action);
  if (!a) return true;
  if (a.includes('add to stock') || a.includes('return') || a.includes('adjust')) {
    return false;
  }
  return true;
}

/** Movements that consume stock outright — it does not come back. */
function isConsumingAction(action) {
  const a = norm(action);
  return (
    a.includes('used') ||
    a.includes('dispose') ||
    a.includes('damage') ||
    a.includes('maintenance')
  );
}

function isReturnAction(action) {
  return norm(action).includes('return');
}

function toQty(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Qty the cleaner typed on the report. Forms have used `qty`, `quantity`, and `used`. */
function reportedQty(row) {
  return toQty(row?.qty ?? row?.quantity ?? row?.quantityUsed ?? row?.used);
}

/** Trims trailing zeros so 1.40 renders as "1.4" and 2.00 as "2". */
export function formatQty(value, unit) {
  if (!value) return '—';
  const rounded = Math.round(value * 100) / 100;
  const text = String(rounded);
  return unit ? `${text}${String(unit).trim()}` : text;
}

function statusFor(row) {
  if (row.returned > 0 && row.used === 0) return { label: 'Returned', tone: 'tone-ok' };
  if (row.used > 0 && row.returned > 0) return { label: 'Partly returned', tone: 'tone-info' };
  if (row.used > 0) return { label: 'Consumed', tone: 'tone-warn' };
  if (row.allocated > 0) return { label: 'Allocated', tone: 'tone-info' };
  return { label: '—', tone: 'tone-mute' };
}

/** Equipment comes back after the job; everything else is used up. */
export const isReusable = (category) => /equip|reus|tool|machine|vacuum/i.test(String(category || ''));

/** Issue movements (handed out to the job), excluding returns / stock-ins. */
export const isIssueMovement = (record) => isIssueLike(record?.updateAction);

/**
 * Project stock movements plus what cleaners reported using, rolled up per tool.
 * Shared by the Overview panel and the Team member view.
 */
export function useProjectResources({ projectId, projectCode, bookingId }) {
  const [rows, setRows] = useState([]);
  const [reported, setReported] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!projectId && !projectCode) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      if (bookingId) {
        graphqlRequest(CLEANER_REPORTS, { bookingId, projectId: projectId || null })
          .then((res) =>
            setReported(
              (res?.approvedWorkReportsForBooking || []).flatMap((r) =>
                reportedResources(r.notesJson).map((row) => ({ ...row, userId: r.userId })),
              ),
            ),
          )
          .catch(() => setReported([]));
      }
      const data = await graphqlRequest(PROJECT_INVENTORY, {
        projectId: projectId || null,
        bookingId: projectId ? null : bookingId || null,
      });
      const all = Array.isArray(data?.getResourcesByProject)
        ? data.getResourcesByProject
        : [];
      setRows(all);
    } catch (err) {
      console.error('Failed to load project resources:', err);
      setFailed(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, projectCode, bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resources = useMemo(() => rollUpResources(rows, reported), [rows, reported]);

  return { rows, reported, resources, loading, failed };
}

/** Movement rows rolled up per tool, with what cleaners reported using. */
export function rollUpResources(rows, reported) {
  const byTool = new Map();
  rows.forEach((record) => {
    const tool = record?.tool || {};
    const key = String(tool.id || tool.name || record.id);
    if (!byTool.has(key)) {
      byTool.set(key, {
        key,
        name: tool.name || record.category || 'Resource',
        category: tool.category || record.category || '',
        code: tool.code || '',
        unit: tool.unit || '',
        allocated: 0,
        used: 0,
        returned: 0,
        notes: [],
      });
    }
    const entry = byTool.get(key);
    const qty = toQty(record.quantityAssigned);
    if (isIssueLike(record.updateAction)) entry.allocated += qty;
    if (isConsumingAction(record.updateAction)) entry.used += qty;
    if (isReturnAction(record.updateAction)) entry.returned += qty;
    const remark = String(record.remark || '').trim();
    if (remark && !entry.notes.includes(remark)) entry.notes.push(remark);
  });

  // What cleaners reported using on their work reports, per tool.
  const keyByRecord = new Map(rows.map((r) => [String(r.id), String(r?.tool?.id || r?.tool?.name || r.id)]));
  const keyByName = new Map(Array.from(byTool.values()).map((e) => [norm(e.name), e.key]));
  const cleanerUsed = new Map();
  reported.forEach((r) => {
    const qty = reportedQty(r);
    let key = keyByRecord.get(String(r?.id)) || keyByName.get(norm(r?.name));
    if (!key && (r?.name || qty)) {
      key = `reported:${norm(r?.name) || r?.id || qty}`;
      if (!byTool.has(key)) {
        byTool.set(key, {
          key,
          name: r.name || 'Resource',
          category: '',
          code: '',
          unit: r.unit || '',
          allocated: toQty(r.assigned),
          used: 0,
          returned: 0,
          notes: [],
        });
      }
    }
    if (!key) return;
    if (qty) cleanerUsed.set(key, (cleanerUsed.get(key) || 0) + qty);
    const condition = r?.status ? `Reported ${norm(r.status)}` : '';
    const entry = byTool.get(key);
    if (condition && entry && !entry.notes.includes(condition)) entry.notes.push(condition);
  });
  // Office "used" movements are authoritative; otherwise show the cleaners' figures.
  byTool.forEach((entry) => {
    if (!entry.used && cleanerUsed.get(entry.key)) entry.used = cleanerUsed.get(entry.key);
  });

  return Array.from(byTool.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export default function ProjectResourcesPanel({ projectId, projectCode, bookingId }) {
  const { resources, loading, failed } = useProjectResources({ projectId, projectCode, bookingId });

  return (
    <div className="pdx-card">
      <h3 className="pdx-card-title">
        <FiBox aria-hidden /> Resources Allocated / Used
      </h3>

      {loading ? (
        <p className="pdx-empty">Loading resources…</p>
      ) : failed ? (
        <p className="pdx-empty">Resource allocations could not be loaded.</p>
      ) : resources.length === 0 ? (
        <p className="pdx-empty">
          No resources have been allocated to this project yet.
        </p>
      ) : (
        <div className="pdx-table-wrap">
          <table className="pdx-table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Allocated</th>
                <th>Used</th>
                <th>Condition / Notes</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((row) => {
                const status = statusFor(row);
                return (
                  <tr key={row.key}>
                    <td>
                      <strong>{row.name}</strong>
                      {row.code ? <small>{row.code}</small> : null}
                    </td>
                    <td className="pdx-num is-accent">
                      {formatQty(row.allocated, row.unit)}
                    </td>
                    <td className="pdx-num is-warn">
                      {formatQty(row.used, row.unit)}
                    </td>
                    <td>{row.notes.length ? row.notes.join(' · ') : '—'}</td>
                    <td>
                      <span className={`pdx-status ${status.tone}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
